import { useCallback, useState } from "react";

import {
  AnalyticsWorkflowError,
  type AnalyticsFailureKind,
} from "../../lib/analyticsWorkflow";
import { cn } from "../../lib/utils";

/**
 * The control that takes an auditor to their workflow's canvas in Optro
 * Analytics.
 *
 * **A button, not an anchor, and that is the whole design.** The destination
 * carries a Cascade session credential in its query string, minted by
 * AuditBoard for the acting user. An `href` would mean holding that credential
 * in the DOM for as long as the dialog is open — readable by anything on the
 * page and captured by anything that serialises it. Instead the URL is fetched
 * when the button is pressed and used once.
 *
 * **Why the blank tab is opened before the fetch.** Browsers allow
 * `window.open` only while a user gesture is still being handled. Opening after
 * an `await` is a pop-up blocker's exact signature, so the tab is claimed
 * synchronously and its location assigned when the URL arrives. `opener` is
 * nulled by hand because the handle has to survive the call — passing
 * `noopener` to `window.open` makes it return `null`, which would leave nothing
 * to navigate.
 *
 * Deliberately absent from these props: any hook, any column id, any knowledge
 * of the analytics routes. `onOpen` answers a URL or `null`, and the component
 * is the same whether that came from the network or a fixture.
 */
interface OpenInAnalyticsLinkProps {
  /**
   * Mint the destination. Resolving `null` means there is nothing to open —
   * the workflow was deleted, or the tenant records no Cascade workbook for it
   * — which is reported to the auditor rather than opening a blank tab onto
   * nothing.
   */
  onOpen: () => Promise<string | null>;
  /** Overrides the default label. The `↗` is part of the copy either way. */
  label?: string;
  /**
   * What a `null` answer is told to the auditor, where this link's destination
   * is narrower than the workflow's own page and the default would misdescribe
   * it. The caller states what could not be opened; the component still owns
   * that it is said at all rather than left as a click that does nothing.
   */
  nothingToOpen?: string;
  /**
   * Route-specific copy for typed analytics failures whose meaning depends on
   * the URL the caller mints. The shared defaults cover only route-invariant
   * failures; callers retain the detail their route established.
   */
  failureMessages?: Partial<Record<AnalyticsFailureKind, string>>;
  className?: string;
}

/** Matches the surrounding hand-off copy, where this label already appears. */
const DEFAULT_LABEL = "Open in Optro Analytics ↗";

const NOTHING_TO_OPEN =
  "This workflow has no page in Optro Analytics. It may have been deleted there.";

const COULD_NOT_OPEN =
  "Could not reach Optro Analytics. Try again in a moment.";

const OPEN_FAILURE_MESSAGES: Partial<Record<AnalyticsFailureKind, string>> = {
  workflow_deleted:
    "This workflow has been deleted and can no longer be opened in Optro Analytics.",
  // The request reached Midship, so this must not read as a connectivity
  // failure. It names no cause because the missing thing varies by route and is
  // not always the resource the link opens — the workspace's integration can be
  // missing too, which neither a retry nor a reload repairs.
  not_found:
    "This workflow could not be opened because something it needs could not be found. If this keeps happening, ask an administrator to check this workspace's Optro integration.",
  permission_denied:
    "You do not have permission to open this workflow in Optro Analytics.",
  conflict:
    "This workflow could not be opened because this workspace has no active Optro Analytics credential. An administrator has to make one available; there is nothing to retry until they do.",
  rejected: "This request was refused, so the workflow could not be opened.",
  session_expired:
    "Your session ended before the workflow could be opened. Sign in again to continue.",
  upstream_unavailable:
    "The analytics service is unavailable right now, so this workflow could not be opened.",
  unreachable: COULD_NOT_OPEN,
};

function openFailureMessage(
  error: unknown,
  failureMessages: Partial<Record<AnalyticsFailureKind, string>>,
): string {
  if (!(error instanceof AnalyticsWorkflowError)) return COULD_NOT_OPEN;
  if (error.kind === "conflict" && error.detail?.trim()) {
    // The backend authors the credential-specific remedy, and a credential that
    // was never created and one that is switched off take different ones. That
    // detail outranks both a caller's override and the shared constant below,
    // so a caller cannot specialize this kind while the response carries a
    // remedy of its own.
    return error.detail;
  }
  return (
    failureMessages[error.kind] ??
    OPEN_FAILURE_MESSAGES[error.kind] ??
    COULD_NOT_OPEN
  );
}

export function OpenInAnalyticsLink({
  onOpen,
  label = DEFAULT_LABEL,
  nothingToOpen = NOTHING_TO_OPEN,
  failureMessages = {},
  className,
}: OpenInAnalyticsLinkProps) {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = useCallback(async () => {
    if (opening) return;
    setError(null);
    // Claimed inside the gesture; see the note on this component.
    const tab = window.open("", "_blank");
    if (tab === null) {
      // The pop-up was blocked before any credential was minted for it — no
      // network call to make, and nothing to close. Checked before `onOpen()`
      // so a blocked pop-up never costs Midship a single-use Cascade
      // credential it cannot deliver anywhere.
      setError(
        "Your browser blocked the new tab. Allow pop-ups for this site, then try again.",
      );
      return;
    }
    tab.opener = null;
    setOpening(true);
    try {
      const url = await onOpen();
      if (url === null || url.trim() === "") {
        tab.close();
        setError(nothingToOpen);
        return;
      }
      tab.location.href = url;
    } catch (error) {
      tab.close();
      console.error("Failed to open workflow in Optro Analytics:", error);
      setError(openFailureMessage(error, failureMessages));
    } finally {
      setOpening(false);
    }
  }, [failureMessages, onOpen, opening, nothingToOpen]);

  return (
    <div className={cn("text-xs", className)}>
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={opening}
        className="text-primary underline underline-offset-2 disabled:opacity-60"
      >
        {opening ? "Opening…" : label}
      </button>
      {error !== null && (
        <p className="text-destructive mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
